/**
 * One read of "what does the analysis say about these files?", shared by every
 * part of the session screen that needs it.
 *
 * Quality is stored per file and there is no session-level aggregate endpoint,
 * so this is N requests. That is the real shape of the data. What it must not
 * be is *2N* requests: the analysis centre and the analysis chain each used to
 * fetch the same assessments independently, and adding a per-card verdict
 * would have made it 3N. The session screen reads them once and hands the
 * result down.
 *
 * **A missing assessment and an unreadable one are different answers.** A 404
 * is `not_assessed` - the normal state of a file nobody has analysed yet. Any
 * other failure is `unknown` for that one file, and the rest of the session
 * still reports honestly. Collapsing the two would let a network blip mark a
 * whole session "not analysed", which is the sentence a user acts on by
 * running an analysis that has already run.
 */

import { useCallback, useMemo } from "react";

import { getQualityAssessment } from "@/api/client";
import { ApiError } from "@/api/errors";
import { useAsync } from "@/lib/useAsync";
import type { MediaAnalysis } from "./mediaAnalysis";

/**
 * How many assessments are read at once.
 *
 * Four, not "all of them": a 200-file session firing 200 parallel requests
 * puts the browser's connection limit between the user and every other request
 * the screen needs, including the media list itself.
 */
export const QUALITY_CONCURRENCY = 4;

export type MediaAnalyses = ReadonlyMap<string, MediaAnalysis>;

export interface SessionQuality {
  /** Keyed by media asset id. Empty until the first read finishes. */
  analyses: MediaAnalyses;
  /** True until the first read finishes. Nothing is claimed before then. */
  loading: boolean;
  /** Re-read every assessment. Called after anything changes them. */
  reload: () => void;
}

export async function loadMediaAnalyses(
  ids: readonly string[],
  signal: AbortSignal,
): Promise<Map<string, MediaAnalysis>> {
  const analyses = new Map<string, MediaAnalysis>();
  for (let offset = 0; offset < ids.length; offset += QUALITY_CONCURRENCY) {
    if (signal.aborted) break;
    const batch = ids.slice(offset, offset + QUALITY_CONCURRENCY);
    const rows = await Promise.all(
      batch.map(async (id): Promise<[string, MediaAnalysis]> => {
        try {
          return [id, { state: "assessed", assessment: await getQualityAssessment(id, { signal }) }];
        } catch (error) {
          if (error instanceof ApiError && error.kind === "not_found") {
            return [id, { state: "not_assessed" }];
          }
          // An aborted read is not an answer about the file. Let it propagate
          // so `useAsync` drops the whole generation instead of writing
          // "durumu okunamadı" onto a session the user simply navigated away
          // from.
          if (error instanceof ApiError && error.kind === "canceled") throw error;
          return [id, { state: "unknown" }];
        }
      }),
    );
    for (const [id, analysis] of rows) analyses.set(id, analysis);
  }
  return analyses;
}

/**
 * The empty result, as one object.
 *
 * A fresh `new Map()` per render would make the returned value different on
 * every render, and the components below it rebuild their work when it
 * changes - which, before this was shared, was a render loop.
 */
const NO_ANALYSES: MediaAnalyses = new Map<string, MediaAnalysis>();

export function useMediaAnalyses(mediaIds: readonly string[]): SessionQuality {
  // The identity of the id list changes on every render of the screen above;
  // its contents do not. Keying on the joined string is what stops the fetch
  // from re-running forever.
  const key = mediaIds.join(",");
  const fetcher = useCallback(
    async (signal: AbortSignal) => ({
      // The result carries the set it describes. Without it, a read still in
      // flight for a newly uploaded file would be answered from the previous
      // read, which has no entry for that file - and "no entry" renders as
      // "analiz durumu okunamadı" on a file nobody has finished reading yet.
      key,
      analyses: await loadMediaAnalyses(key ? key.split(",") : [], signal),
    }),
    [key],
  );
  const { state, reload } = useAsync(fetcher);
  const current = state.data?.key === key ? state.data.analyses : null;
  const analyses = current ?? NO_ANALYSES;
  const loading = current === null && state.status !== "error";
  // Stable while the answers are stable, so a consumer can depend on the
  // whole result rather than on a hand-rolled summary of it.
  return useMemo(() => ({ analyses, loading, reload }), [analyses, loading, reload]);
}

/** What is known about one file. `unknown` is the honest answer for a gap. */
export function analysisOf(quality: SessionQuality, mediaId: string): MediaAnalysis {
  return quality.analyses.get(mediaId) ?? { state: "unknown" };
}
